import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal, ModalOverlay, ModalContent, Input, Box, Text, VStack, HStack, Kbd, InputGroup, InputLeftElement,
} from '@chakra-ui/react';
import { useDocs } from '../docs/DocsContext';
import { headingId } from '../lib/slug';

// 문서가 30편 안팎이라 전체를 한 번 받아 색인한다. 피드 모드는 /bundle 한 번, 정적 모드는 파일별 fetch.
// 늘어나면 빌드 시 색인 파일을 만들 것.
function useIndex(isOpen, pages, loadBundle) {
  const [index, setIndex] = useState([]);
  useEffect(() => { setIndex([]); }, [pages, loadBundle]);
  useEffect(() => {
    if (!isOpen || index.length) return;
    loadBundle().catch(() => []).then((docs) => {
      const rows = [];
      docs.forEach(({ page, text }) => {
        rows.push({ page, title: page.title, sub: page.group, hash: null, hay: (page.title + ' ' + page.group + ' ' + text).toLowerCase() });
        text.split('\n').filter((l) => l.startsWith('## ')).forEach((line) => {
          const title = line.replace(/^##\s+/, '').trim();
          rows.push({ page, title, sub: page.title, hash: headingId(title), hay: (title + ' ' + text).toLowerCase() });
        });
      });
      setIndex(rows);
    });
  }, [isOpen, index.length, pages, loadBundle]);
  return index;
}

export default function SearchPalette({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { visiblePages: pages, loadBundle } = useDocs();
  const index = useIndex(isOpen, pages, loadBundle);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { if (isOpen) { setQ(''); setSel(0); } }, [isOpen]);

  const hits = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return index.slice(0, 8);
    return index.filter((r) => r.hay.includes(query)).slice(0, 12);
  }, [q, index]);

  const open = (hit) => {
    if (!hit) return;
    onClose();
    navigate(`/${hit.page.slug}`);
    if (hit.hash) {
      setTimeout(() => document.getElementById(hit.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); open(hits[sel]); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} initialFocusRef={inputRef} size="xl" motionPreset="none">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(3px)" />
      <ModalContent mt="12vh" borderRadius="12px" overflow="hidden" borderWidth="1px" borderColor="neutral.200">
        <InputGroup borderBottomWidth="1px" borderColor="neutral.200">
          <InputLeftElement pointerEvents="none" color="neutral.400" h="56px" pl={2}>⌕</InputLeftElement>
          <Input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }}
            onKeyDown={onKeyDown} placeholder="찾으시는 내용을 입력하세요" h="56px" border="none"
            _focusVisible={{ boxShadow: 'none' }} fontSize="15px" />
        </InputGroup>

        <Box maxH="min(420px, 52vh)" overflowY="auto" p={2}>
          {hits.length === 0 ? (
            <Text py={10} textAlign="center" fontSize="13.5px" color="neutral.500">
              “{q.trim()}” 에 대한 결과가 없습니다
            </Text>
          ) : (
            <VStack align="stretch" spacing={0.5}>
              {hits.map((hit, i) => (
                <Box key={`${hit.page.slug}-${hit.hash || 'page'}-${i}`} as="button" textAlign="left"
                  px={3} py={2.5} borderRadius="8px" bg={i === sel ? 'brand.50' : 'transparent'}
                  onMouseEnter={() => setSel(i)} onClick={() => open(hit)}>
                  <Text fontSize="14px" fontWeight="500" color={i === sel ? 'brand.700' : 'neutral.800'}>{hit.title}</Text>
                  <Text fontSize="12px" color="neutral.500" noOfLines={1}>{hit.sub}</Text>
                </Box>
              ))}
            </VStack>
          )}
        </Box>

        <HStack spacing={4} px={4} py={2.5} borderTopWidth="1px" borderColor="neutral.200"
          bg="neutral.100" fontSize="11px" color="neutral.500">
          <HStack spacing={1}><Kbd>↑</Kbd><Kbd>↓</Kbd><Text>이동</Text></HStack>
          <HStack spacing={1}><Kbd>↵</Kbd><Text>열기</Text></HStack>
          <HStack spacing={1}><Kbd>esc</Kbd><Text>닫기</Text></HStack>
        </HStack>
      </ModalContent>
    </Modal>
  );
}
